/**
 * Server-side validators for the lobby's theming primitives.
 *
 * The launch wizard, profile modal, and live 3D scene all share a
 * single vocabulary (`PlinthMaterialId`, `SkyboxTheme`) defined in
 * `features/lobby/theming`. API routes accept these as free-form
 * strings on the wire — this module checks them against the canonical
 * registry so we never persist an unknown value that the renderer
 * would fall back to a default for.
 *
 * Kept free of any React / THREE imports so server routes can use it
 * without pulling the 3D stack into the Node bundle.
 */

import { PLINTH_MATERIAL_BY_ID } from '@/features/lobby/theming/plinths/materials';
import type { PlinthMaterialId } from '@/features/lobby/theming/plinths/types';
import { SKYBOX_THEMES, type SkyboxTheme } from '@/features/lobby/theming/skyboxes/types';

/**
 * Sentinel meaning "the creator explicitly picked None" — distinct from a
 * missing/legacy value, which still falls back to an adapter-derived
 * default in the lobby. Persisted verbatim to `pool_metadata` so the
 * `COALESCE`-based UPSERT in `setPoolMetadata` can carry it through
 * without bespoke null-clearing SQL.
 */
export const THEMING_NONE_SENTINEL = 'none' as const;
export type ThemingNoneSentinel = typeof THEMING_NONE_SENTINEL;

/** Returns the value as a `PlinthMaterialId` if it's registered, the
 *  `'none'` sentinel when the wizard's None tile was picked, else null. */
export function coercePlinthMaterialId(
  value: unknown,
): PlinthMaterialId | ThemingNoneSentinel | null {
  if (typeof value !== 'string') return null;
  if (value === THEMING_NONE_SENTINEL) return THEMING_NONE_SENTINEL;
  return value in PLINTH_MATERIAL_BY_ID ? (value as PlinthMaterialId) : null;
}

/** Returns the value as a `SkyboxTheme` if it's registered, the `'none'`
 *  sentinel when the wizard's None tile was picked, else null. */
export function coerceSkyboxTheme(
  value: unknown,
): SkyboxTheme | ThemingNoneSentinel | null {
  if (typeof value !== 'string') return null;
  if (value === THEMING_NONE_SENTINEL) return THEMING_NONE_SENTINEL;
  return (SKYBOX_THEMES as readonly string[]).includes(value)
    ? (value as SkyboxTheme)
    : null;
}
